/**
 * City Agency Pages — Wave 12
 * Xi'an, Dalian, Suzhou (China), Pune (India), Cebu (Philippines),
 * Medina (KSA), Port Harcourt (Nigeria), Curitiba (Brazil), Kraków (Poland), Marseille (France)
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards using React, TypeScript, and cloud infrastructure.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, monitoring.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance.' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools with enterprise-grade security.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations, HIPAA-compliant apps.' },
  { name: 'SaaS & Technology', description: 'Product development, scaling, and modernization for SaaS companies.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, tenant portals.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse management platforms.' },
];

export const CITY_AGENCY_PAGES_WAVE12: Record<string, CityAgencyData> = {

  xian: {
    slug: 'software-development-agency-xian',
    cityName: "Xi'an",
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY',
    title: {
      en: "Software Development Agency Xi'an | Call IT Dev",
      fr: "Agence Développement Logiciel Xi'an | Call IT Dev",
      zh: '西安软件开发公司 | Call IT Dev',
      de: "Softwareentwicklung Agentur Xi'an | Call IT Dev",
      es: "Agencia Desarrollo Software Xi'an | Call IT Dev",
    },
    description: {
      en: "Software development agency in Xi'an, China. Custom SaaS, web, mobile, AI. Top STEM talent from Northwestern Polytechnical University. From $18/hr.",
      fr: "Agence de développement logiciel à Xi'an, Chine. SaaS, web, mobile, IA. Talents STEM de premier ordre, dès 18 $/h.",
      zh: '西安软件开发公司。定制SaaS、Web、移动端、AI开发。西北工业大学顶尖人才，18美元/小时起。',
      de: "Softwareentwicklungsagentur in Xi'an, China. SaaS, Web, Mobile, KI. Top-STEM-Talente, ab 18 $/h.",
      es: "Agencia de desarrollo de software en Xi'an, China. SaaS, web, móvil, IA. Desde 18 $/h.",
    },
    keywords: ["software development Xi'an", '西安软件开发', "IT outsourcing Xi'an", "web development Xi'an"],
    heroTitle: {
      en: "Software Development Agency in Xi'an",
      fr: "Agence de Développement Logiciel à Xi'an",
      zh: '西安软件开发公司',
      de: "Softwareentwicklung Agentur in Xi'an",
      es: "Agencia de Desarrollo de Software en Xi'an",
    },
    heroSubtitle: {
      en: "Xi'an's deep engineering heritage meets modern tech. Top graduates from Northwestern Polytechnical University, competitive rates, strong aerospace & defense tech ecosystem.",
      fr: "L'héritage ingénieur de Xi'an au service du tech moderne. Diplômés de premier plan, tarifs compétitifs.",
      zh: '西安深厚的工程传统与现代科技结合。西工大顶尖毕业生，极具竞争力的价格。',
      de: "Xi'ans tiefes Ingenieur-Erbe trifft moderne Technologie. Top-Absolventen, wettbewerbsfähige Preise.",
      es: "La herencia ingenieril de Xi'an se une a la tecnología moderna. Graduados de primer nivel.",
    },
    whyTitle: {
      en: "Why Xi'an for Software Development",
      fr: "Pourquoi Xi'an pour le Développement Logiciel",
      zh: '为什么选择西安进行软件开发',
      de: "Warum Xi'an für Softwareentwicklung",
      es: "Por Qué Xi'an para Desarrollo de Software",
    },
    whyReasons: [
      { icon: '🎓', title: 'Elite Universities', description: "Northwestern Polytechnical & Xi'an Jiaotong University produce world-class engineers in AI, aerospace, and systems." },
      { icon: '💰', title: '30-40% Below Tier 1', description: "Xi'an rates are significantly lower than Beijing/Shanghai while maintaining equivalent quality." },
      { icon: '🏗️', title: 'Hi-Tech Zone', description: "Xi'an Hi-Tech Industries Development Zone hosts 2,000+ tech companies and major R&D centers." },
      { icon: '🛡️', title: 'Defense Tech Heritage', description: 'Deep expertise in systems engineering, embedded software, and mission-critical applications.' },
      { icon: '🚄', title: 'HSR Connected', description: 'High-speed rail connects to Beijing (4.5h), Shanghai (6h), Chengdu (3h).' },
      { icon: '🌐', title: 'Belt & Road Hub', description: "Xi'an is a strategic hub for Belt & Road tech projects across Central Asia." },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: "Why Xi'an for software development?", answer: "Xi'an offers elite STEM talent from top universities, 30-40% lower costs than Beijing/Shanghai, and a strong defense/aerospace tech ecosystem." },
      { question: 'What technologies do your Xi\'an engineers work with?', answer: 'React, TypeScript, Python, Java, Go, C++, embedded systems, AI/ML, cloud-native. Specialty in systems engineering.' },
      { question: "How does Xi'an compare to Tier 1 Chinese cities?", answer: "30-40% lower costs with equivalent quality. Xi'an has lower employee turnover and strong university pipelines." },
      { question: 'Can you handle defense/government projects?', answer: 'Yes, our Xi\'an team has deep experience in mission-critical, high-security systems with appropriate certifications.' },
      { question: 'What is the team setup timeline?', answer: 'Initial team operational in 2-3 weeks. We can provide CVs within 5 business days.' },
      { question: 'Do you support IP protection?', answer: 'Full IP assignment, NDAs, and compliance with Chinese cybersecurity law and international standards.' },
    ],
    localRate: '¥400-600',
    ourRate: '$18-30',
    savings: '55%+',
  },

  dalian: {
    slug: 'software-development-agency-dalian',
    cityName: 'Dalian',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY',
    title: {
      en: 'Software Development Agency Dalian | Call IT Dev',
      fr: 'Agence Développement Logiciel Dalian | Call IT Dev',
      zh: '大连软件开发公司 | Call IT Dev',
      ja: '大連ソフトウェア開発会社 | Call IT Dev',
    },
    description: {
      en: "Software development agency in Dalian, China. Japan-facing IT hub, strong in enterprise systems. 200+ engineers, from $16/hr.",
      fr: "Agence de développement logiciel à Dalian, Chine. Hub IT tourné vers le Japon, systèmes d'entreprise, dès 16 $/h.",
      zh: '大连软件开发公司。面向日本的IT中心，企业系统强项。200+工程师，16美元/小时起。',
      ja: '大連のソフトウェア開発会社。日本向けITハブ、エンタープライズシステムに強み。16$/時間〜。',
    },
    keywords: ['software development Dalian', '大连软件开发', 'IT outsourcing Dalian', '大連 ソフトウェア開発'],
    heroTitle: {
      en: 'Software Development Agency in Dalian',
      fr: 'Agence de Développement Logiciel à Dalian',
      zh: '大连软件开发公司',
      ja: '大連のソフトウェア開発会社',
    },
    heroSubtitle: {
      en: "China's premier Japan-facing IT outsourcing hub. Bilingual JP/EN engineers, deep enterprise expertise, coastal city with excellent quality of life.",
      fr: "Premier hub IT chinois tourné vers le Japon. Ingénieurs bilingues JP/EN, expertise entreprise.",
      zh: '中国首选的面向日本IT外包中心。日英双语工程师，深厚企业经验。',
      ja: '中国有数の対日ITアウトソーシング拠点。日英バイリンガルエンジニア、深い企業向け専門性。',
    },
    whyTitle: {
      en: 'Why Dalian for Software Development',
      fr: 'Pourquoi Dalian pour le Développement Logiciel',
      zh: '为什么选择大连进行软件开发',
      ja: 'なぜ大連でソフトウェア開発か',
    },
    whyReasons: [
      { icon: '🇯🇵', title: 'Japan IT Hub', description: 'Dalian is China\'s #1 Japan-facing IT outsourcing city with 500+ Japanese-affiliated tech companies.' },
      { icon: '🗣️', title: 'JP/EN Bilingual', description: 'Large pool of Japanese-speaking engineers (JLPT N1/N2). Perfect for Japanese market projects.' },
      { icon: '💰', title: 'Cost Efficient', description: '60-70% savings vs Tokyo rates. Dalian rates are also 20-30% below Beijing/Shanghai.' },
      { icon: '🏖️', title: 'Quality of Life', description: 'Coastal city with low turnover rates. Engineers stay 3x longer than in Tier 1 cities.' },
      { icon: '🎓', title: 'Strong Universities', description: 'Dalian University of Technology and DUT-RU joint programs produce excellent engineers.' },
      { icon: '✈️', title: 'Direct Flights', description: 'Direct flights to Tokyo (2.5h), Seoul (1.5h), and all major Chinese cities.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why is Dalian popular for Japanese companies?', answer: 'Historical ties, 500+ Japanese tech companies, large pool of JLPT N1/N2 certified engineers, and 2.5h direct flight to Tokyo.' },
      { question: 'Do your Dalian engineers speak Japanese?', answer: 'Yes, we have 50+ engineers with JLPT N1/N2 certification. Documentation and communication in Japanese is standard.' },
      { question: 'What is the cost comparison to Tokyo?', answer: 'Senior developers at $16-28/hr vs ¥8,000-15,000/hr in Tokyo. 60-70% savings with equivalent quality.' },
      { question: 'Can Dalian teams work on non-Japanese projects?', answer: 'Absolutely. Our Dalian team also serves US, EU, and APAC clients in English.' },
      { question: 'What about data security?', answer: 'Enterprise-grade security, ISMS compliant, with options for on-premise or private cloud deployment.' },
      { question: 'How fast can you start?', answer: 'Team setup in 2 weeks. CVs and technical interviews within 5 business days.' },
    ],
    localRate: '¥350-550',
    ourRate: '$16-28',
    savings: '65%+',
  },

  suzhou: {
    slug: 'software-development-agency-suzhou',
    cityName: 'Suzhou',
    countryName: 'China',
    flag: '🇨🇳',
    timezone: 'CST (GMT+8)',
    currency: 'CNY',
    title: {
      en: 'Software Development Agency Suzhou | Call IT Dev',
      fr: 'Agence Développement Logiciel Suzhou | Call IT Dev',
      zh: '苏州软件开发公司 | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Suzhou, China. Industrial IoT & manufacturing tech. Adjacent to Shanghai, from $20/hr.',
      fr: 'Agence de développement logiciel à Suzhou, Chine. IoT industriel & tech manufacturing, dès 20 $/h.',
      zh: '苏州软件开发公司。工业物联网和制造业科技。毗邻上海，20美元/小时起。',
    },
    keywords: ['software development Suzhou', '苏州软件开发', 'IoT development Suzhou', 'manufacturing tech Suzhou'],
    heroTitle: {
      en: 'Software Development Agency in Suzhou',
      fr: 'Agence de Développement Logiciel à Suzhou',
      zh: '苏州软件开发公司',
    },
    heroSubtitle: {
      en: 'Suzhou Industrial Park — China\'s premier smart manufacturing hub. IoT, MES, and Industry 4.0 specialists. 25 min HSR from Shanghai.',
      fr: 'Parc Industriel de Suzhou — hub smart manufacturing chinois. Spécialistes IoT et Industrie 4.0. 25 min de Shanghai.',
      zh: '苏州工业园区——中国领先的智能制造中心。IoT、MES和工业4.0专家。距上海高铁25分钟。',
    },
    whyTitle: {
      en: 'Why Suzhou for Software Development',
      fr: 'Pourquoi Suzhou pour le Développement Logiciel',
      zh: '为什么选择苏州进行软件开发',
    },
    whyReasons: [
      { icon: '🏭', title: 'Industry 4.0 Hub', description: 'Suzhou Industrial Park hosts 5,000+ companies including many Fortune 500 R&D centers.' },
      { icon: '🔗', title: 'IoT & MES Expertise', description: 'Deep specialization in industrial IoT, manufacturing execution systems, and smart factory solutions.' },
      { icon: '🚄', title: '25 Min from Shanghai', description: 'High-speed rail to Shanghai in 25 minutes. Access to Shanghai talent pool at Suzhou prices.' },
      { icon: '💰', title: '20-30% Below Shanghai', description: 'Suzhou rates are significantly lower than Shanghai while offering comparable quality.' },
      { icon: '🌿', title: 'Talent Retention', description: 'Excellent quality of life means lower turnover. Engineers stay longer, reducing project risk.' },
      { icon: '🏗️', title: 'Singapore Partnership', description: 'Suzhou Industrial Park is a Singapore-China joint venture with world-class infrastructure.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'What makes Suzhou unique for tech?', answer: 'Suzhou Industrial Park is a Singapore-China joint venture hosting 5,000+ companies. Deep IoT and manufacturing tech expertise.' },
      { question: 'How close is Suzhou to Shanghai?', answer: '25 minutes by high-speed rail. Many engineers live in Suzhou and access Shanghai\'s ecosystem daily.' },
      { question: 'What industries does your Suzhou team specialize in?', answer: 'Manufacturing, IoT, Industry 4.0, automotive, electronics, and biotech — aligned with the SIP ecosystem.' },
      { question: 'Cost comparison to Shanghai?', answer: '20-30% lower than Shanghai rates with access to the same talent pool via HSR.' },
      { question: 'Can you integrate with existing MES/ERP systems?', answer: 'Yes. Our Suzhou team has deep experience with SAP, Oracle, Siemens, and custom MES integrations.' },
      { question: 'Minimum engagement?', answer: 'Start with 2 developers. Scale to 30+. Flexible T&M and dedicated team models.' },
    ],
    localRate: '¥450-700',
    ourRate: '$20-32',
    savings: '50%+',
  },

  pune: {
    slug: 'software-development-agency-pune',
    cityName: 'Pune',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (GMT+5:30)',
    currency: 'INR/USD',
    title: {
      en: 'Software Development Agency Pune | Call IT Dev',
      fr: 'Agence Développement Logiciel Pune | Call IT Dev',
      de: 'Softwareentwicklung Agentur Pune | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Pune, India. IT hub with 2M+ tech professionals. Custom SaaS, AI, cloud. From $15/hr.',
      fr: 'Agence de développement logiciel à Pune, Inde. Hub IT avec 2M+ professionnels tech, dès 15 $/h.',
      de: 'Softwareentwicklungsagentur in Pune, Indien. IT-Hub mit 2M+ Tech-Profis, ab 15 $/h.',
    },
    keywords: ['software development Pune', 'IT outsourcing Pune', 'web development Pune India', 'app development Pune'],
    heroTitle: {
      en: 'Software Development Agency in Pune',
      fr: 'Agence de Développement Logiciel à Pune',
      de: 'Softwareentwicklung Agentur in Pune',
    },
    heroSubtitle: {
      en: "India's #2 IT hub after Bangalore. 2M+ tech professionals, world-class engineering colleges, 70% savings vs US/EU rates.",
      fr: "2e hub IT indien après Bangalore. 2M+ professionnels tech, 70% d'économies vs USA/UE.",
      de: 'Indiens #2 IT-Hub nach Bangalore. 2M+ Tech-Profis, 70% Ersparnis vs US/EU.',
    },
    whyTitle: {
      en: 'Why Pune for Software Development',
      fr: 'Pourquoi Pune pour le Développement Logiciel',
      de: 'Warum Pune für Softwareentwicklung',
    },
    whyReasons: [
      { icon: '🎓', title: 'Education Capital', description: 'Pune has 800+ engineering colleges. Massive STEM talent pipeline.' },
      { icon: '💰', title: '70% Cost Savings', description: '$15-28/hr vs $80-150/hr in the US. Top-tier quality at Indian rates.' },
      { icon: '🏢', title: 'IT Hub', description: 'Hinjewadi IT Park hosts TCS, Infosys, Wipro, and 1,000+ tech companies.' },
      { icon: '🗣️', title: 'English Native', description: 'All engineers are fluent English speakers. Zero language barrier.' },
      { icon: '✈️', title: 'Well Connected', description: 'New international airport, direct flights to Dubai, Singapore, London.' },
      { icon: '🌡️', title: 'Great Climate', description: 'Pleasant climate year-round means higher productivity and lower turnover.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Pune over Bangalore?', answer: 'Pune offers 15-20% lower costs than Bangalore, lower attrition rates, and the same quality talent pool. Many engineers prefer Pune\'s quality of life.' },
      { question: 'What is the timezone overlap with Europe?', answer: 'IST is GMT+5:30. 3-4 hours overlap with CET. We offer flexible schedules for European clients.' },
      { question: 'Can you scale teams quickly?', answer: 'Yes. Pune\'s massive talent pool allows scaling from 2 to 50+ engineers within 4-6 weeks.' },
      { question: 'What about data protection?', answer: 'Enterprise-grade security, GDPR compliant. India\'s DPDP Act provides additional protections.' },
      { question: 'Do you offer fixed-price projects?', answer: 'Yes. T&M, fixed-price, and dedicated team models available. Discovery phase for fixed-price quotes.' },
      { question: 'How do you handle IP?', answer: 'Full IP assignment to client. NDAs, IP agreements, and source code escrow available.' },
    ],
    localRate: '$60-120',
    ourRate: '$15-28',
    savings: '70%+',
  },

  cebu: {
    slug: 'software-development-agency-cebu',
    cityName: 'Cebu',
    countryName: 'Philippines',
    flag: '🇵🇭',
    timezone: 'PHT (GMT+8)',
    currency: 'PHP/USD',
    title: {
      en: 'Software Development Agency Cebu | Call IT Dev',
      fr: 'Agence Développement Logiciel Cebu | Call IT Dev',
      ja: 'セブ島ソフトウェア開発会社 | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Cebu, Philippines. English-native engineers, US-aligned culture. From $14/hr.',
      fr: 'Agence de développement logiciel à Cebu, Philippines. Ingénieurs anglophones natifs, dès 14 $/h.',
      ja: 'セブ島のソフトウェア開発会社。英語ネイティブ、米国文化対応、14$/時間〜。',
    },
    keywords: ['software development Cebu', 'IT outsourcing Cebu Philippines', 'web development Cebu', 'app development Philippines'],
    heroTitle: {
      en: 'Software Development Agency in Cebu',
      fr: 'Agence de Développement Logiciel à Cebu',
      ja: 'セブ島のソフトウェア開発会社',
    },
    heroSubtitle: {
      en: "Philippines' rising tech hub. English-native developers, US-aligned work culture, 75% savings. The next frontier of IT outsourcing.",
      fr: "Hub tech émergent des Philippines. Développeurs anglophones natifs, culture US, 75% d'économies.",
      ja: 'フィリピンの新興テックハブ。英語ネイティブ、米国文化、75%コスト削減。',
    },
    whyTitle: {
      en: 'Why Cebu for Software Development',
      fr: 'Pourquoi Cebu pour le Développement Logiciel',
      ja: 'なぜセブ島でソフトウェア開発か',
    },
    whyReasons: [
      { icon: '🗣️', title: 'English Native', description: 'Philippines has the 3rd largest English-speaking population globally. Zero accent barrier.' },
      { icon: '💰', title: '75% Cost Savings', description: '$14-25/hr vs $80-150/hr in the US. Exceptional value for quality work.' },
      { icon: '🇺🇸', title: 'US-Aligned Culture', description: 'Strong American cultural affinity. Familiar with US business practices and expectations.' },
      { icon: '🏢', title: 'IT Park Ecosystem', description: 'Cebu IT Park and Cebu Business Park host 200+ tech companies and BPO firms.' },
      { icon: '🎓', title: 'Growing Talent', description: 'University of the Philippines Cebu, USC, and Cebu Institute of Technology produce strong graduates.' },
      { icon: '🌴', title: 'Low Turnover', description: 'Island lifestyle and lower cost of living mean engineers stay longer.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Cebu over Manila?', answer: 'Cebu offers 10-15% lower costs, lower attrition, and a better quality of life. Growing tech ecosystem with less competition for talent.' },
      { question: 'What is the timezone overlap with the US?', answer: 'PHT is GMT+8. Same day as US West Coast. Night shift available for real-time US collaboration.' },
      { question: 'Do Cebu engineers speak native English?', answer: 'Yes. Philippines is the world\'s 3rd largest English-speaking country. All engineers are fluent with neutral accents.' },
      { question: 'Can you handle enterprise projects?', answer: 'Yes. Our Cebu team handles projects for Fortune 500 companies with full security compliance.' },
      { question: 'What about natural disasters?', answer: 'We have BCP/DRP plans, redundant power, backup internet, and geographically distributed teams.' },
      { question: 'Minimum team size?', answer: 'Start with 2 developers. Scale flexibly based on project needs.' },
    ],
    localRate: '$60-120',
    ourRate: '$14-25',
    savings: '75%+',
  },

  medina: {
    slug: 'software-development-agency-medina',
    cityName: 'Medina',
    countryName: 'Saudi Arabia',
    flag: '🇸🇦',
    timezone: 'AST (GMT+3)',
    currency: 'SAR/USD',
    title: {
      en: 'Software Development Agency Medina | Call IT Dev',
      fr: 'Agence Développement Logiciel Médine | Call IT Dev',
      ar: 'شركة تطوير برمجيات المدينة المنورة | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Medina, Saudi Arabia. Vision 2030 aligned, Arabic/English bilingual engineers. From $20/hr.',
      fr: 'Agence de développement logiciel à Médine, Arabie saoudite. Vision 2030, ingénieurs bilingues, dès 20 $/h.',
      ar: 'شركة تطوير برمجيات في المدينة المنورة. متوافقة مع رؤية 2030، مهندسون ثنائيو اللغة، من 20$/ساعة.',
    },
    keywords: ['software development Medina', 'IT outsourcing Saudi Arabia', 'تطوير برمجيات المدينة المنورة', 'Vision 2030 tech'],
    heroTitle: {
      en: 'Software Development Agency in Medina',
      fr: 'Agence de Développement Logiciel à Médine',
      ar: 'شركة تطوير برمجيات في المدينة المنورة',
    },
    heroSubtitle: {
      en: "Supporting Saudi Arabia's Vision 2030 digital transformation. Arabic/English engineers, Sharia-compliant fintech expertise, smart city solutions.",
      fr: "Accompagnant la transformation digitale Vision 2030. Ingénieurs arabe/anglais, expertise fintech conforme à la charia.",
      ar: 'دعم التحول الرقمي لرؤية 2030. مهندسون عرب/إنجليز، خبرة في التكنولوجيا المالية المتوافقة مع الشريعة.',
    },
    whyTitle: {
      en: 'Why Medina for Software Development',
      fr: 'Pourquoi Médine pour le Développement Logiciel',
      ar: 'لماذا المدينة المنورة لتطوير البرمجيات',
    },
    whyReasons: [
      { icon: '🏛️', title: 'Vision 2030', description: 'Medina is a key Vision 2030 smart city hub with massive government tech investment.' },
      { icon: '🗣️', title: 'Arabic/English Bilingual', description: 'Native Arabic speakers with strong English proficiency. Perfect for GCC market projects.' },
      { icon: '💰', title: 'Competitive Rates', description: '50-60% savings vs Riyadh/Dubai rates for equivalent quality.' },
      { icon: '🕌', title: 'Islamic Finance Expertise', description: 'Deep knowledge of Sharia-compliant fintech, halal e-commerce, and Islamic banking systems.' },
      { icon: '🏗️', title: 'Smart City Projects', description: 'Medina Knowledge Economic City and smart pilgrimage management systems.' },
      { icon: '🌐', title: 'GCC Market Access', description: 'Strategic position for projects targeting Saudi Arabia and broader GCC markets.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Medina for tech projects?', answer: 'Medina is a Vision 2030 smart city hub with government tech investment, growing talent pool, and lower costs than Riyadh.' },
      { question: 'Do you handle Sharia-compliant fintech?', answer: 'Yes. Our team has expertise in Islamic banking systems, halal e-commerce, and Sharia-compliant payment platforms.' },
      { question: 'What about data residency?', answer: 'We support Saudi data residency requirements and comply with NCA cybersecurity framework.' },
      { question: 'Can you work on government projects?', answer: 'Yes, with appropriate certifications and security clearances for Saudi government contracts.' },
      { question: 'Cost comparison to Riyadh?', answer: '30-40% lower than Riyadh rates while maintaining quality standards.' },
      { question: 'How fast can you start?', answer: 'Team setup in 2-3 weeks. Faster for Arabic-language projects.' },
    ],
    localRate: 'SAR 300-500',
    ourRate: '$20-35',
    savings: '55%+',
  },

  portHarcourt: {
    slug: 'software-development-agency-port-harcourt',
    cityName: 'Port Harcourt',
    countryName: 'Nigeria',
    flag: '🇳🇬',
    timezone: 'WAT (GMT+1)',
    currency: 'NGN/USD',
    title: {
      en: 'Software Development Agency Port Harcourt | Call IT Dev',
      fr: 'Agence Développement Logiciel Port Harcourt | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Port Harcourt, Nigeria. Oil & gas tech, fintech, English-native engineers. From $12/hr.',
      fr: 'Agence de développement logiciel à Port Harcourt, Nigeria. Tech pétrole & gaz, fintech, dès 12 $/h.',
    },
    keywords: ['software development Port Harcourt', 'IT outsourcing Nigeria', 'oil gas tech Port Harcourt', 'fintech Nigeria'],
    heroTitle: {
      en: 'Software Development Agency in Port Harcourt',
      fr: 'Agence de Développement Logiciel à Port Harcourt',
    },
    heroSubtitle: {
      en: "Nigeria's oil capital meets tech innovation. English-native engineers, oil & gas digitization expertise, 80% savings vs Western rates.",
      fr: "La capitale pétrolière du Nigeria rencontre l'innovation tech. Ingénieurs anglophones natifs, 80% d'économies.",
    },
    whyTitle: {
      en: 'Why Port Harcourt for Software Development',
      fr: 'Pourquoi Port Harcourt pour le Développement Logiciel',
    },
    whyReasons: [
      { icon: '🛢️', title: 'Oil & Gas Tech Hub', description: 'Deep expertise in energy sector digitization, SCADA systems, and upstream/downstream tech.' },
      { icon: '🗣️', title: 'English Native', description: 'Nigeria is Africa\'s largest English-speaking country. Zero language barrier.' },
      { icon: '💰', title: '80% Cost Savings', description: '$12-22/hr vs $80-150/hr in US/UK. Exceptional value for quality engineering.' },
      { icon: '🏦', title: 'Fintech Growth', description: 'Nigeria\'s fintech boom drives strong payment and banking tech expertise.' },
      { icon: '⏰', title: 'CET Aligned', description: 'WAT (GMT+1) aligns perfectly with Central European Time for real-time collaboration.' },
      { icon: '🎓', title: 'University Pipeline', description: 'University of Port Harcourt and Rivers State University produce strong CS graduates.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Port Harcourt?', answer: 'Port Harcourt combines oil & gas tech expertise, English-native engineers, CET timezone alignment, and 80% cost savings.' },
      { question: 'What oil & gas tech do you build?', answer: 'SCADA dashboards, HSE management systems, production monitoring, asset management, and upstream analytics platforms.' },
      { question: 'Is internet reliable?', answer: 'Our office has redundant fiber connections, backup generators, and Starlink as tertiary backup.' },
      { question: 'What about security?', answer: 'Enterprise Security Standards, encrypted communications, and secure development environments.' },
      { question: 'Can you scale?', answer: 'Start with 2 developers. Scale to 20+ leveraging Port Harcourt and Lagos talent pools.' },
      { question: 'How do payments work?', answer: 'USD invoicing, wire transfer, or major payment platforms. Transparent pricing with no hidden fees.' },
    ],
    localRate: '$60-120',
    ourRate: '$12-22',
    savings: '80%+',
  },

  curitiba: {
    slug: 'software-development-agency-curitiba',
    cityName: 'Curitiba',
    countryName: 'Brazil',
    flag: '🇧🇷',
    timezone: 'BRT (GMT-3)',
    currency: 'BRL/USD',
    title: {
      en: 'Software Development Agency Curitiba | Call IT Dev',
      fr: 'Agence Développement Logiciel Curitiba | Call IT Dev',
      pt: 'Agência Desenvolvimento Software Curitiba | Call IT Dev',
      es: 'Agencia Desarrollo Software Curitiba | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Curitiba, Brazil. Smart city pioneer, strong automotive & IoT tech. From $18/hr.',
      fr: 'Agence de développement logiciel à Curitiba, Brésil. Pionnière ville intelligente, tech auto & IoT, dès 18 $/h.',
      pt: 'Agência de desenvolvimento de software em Curitiba, Brasil. Pioneira em cidade inteligente, tech auto & IoT, a partir de R$90/h.',
      es: 'Agencia de desarrollo de software en Curitiba, Brasil. Pionera en ciudad inteligente, tech auto & IoT, desde 18 $/h.',
    },
    keywords: ['software development Curitiba', 'desenvolvimento software Curitiba', 'IT outsourcing Curitiba Brazil', 'nearshore development Brazil'],
    heroTitle: {
      en: 'Software Development Agency in Curitiba',
      fr: 'Agence de Développement Logiciel à Curitiba',
      pt: 'Agência de Desenvolvimento de Software em Curitiba',
      es: 'Agencia de Desarrollo de Software en Curitiba',
    },
    heroSubtitle: {
      en: "Brazil's most innovative city. Smart city pioneer, automotive tech hub, excellent quality of life. US-timezone nearshore at Brazilian rates.",
      fr: "La ville la plus innovante du Brésil. Pionnière smart city, hub auto tech, nearshore fuseau US.",
      pt: 'A cidade mais inovadora do Brasil. Pioneira em cidade inteligente, hub de tech automotiva, nearshore fuso US.',
      es: 'La ciudad más innovadora de Brasil. Pionera en ciudad inteligente, hub tech automotriz.',
    },
    whyTitle: {
      en: 'Why Curitiba for Software Development',
      fr: 'Pourquoi Curitiba pour le Développement Logiciel',
      pt: 'Por Que Curitiba para Desenvolvimento de Software',
      es: 'Por Qué Curitiba para Desarrollo de Software',
    },
    whyReasons: [
      { icon: '🏙️', title: 'Smart City Pioneer', description: 'Curitiba pioneered smart urban planning. Deep expertise in IoT, mobility, and urban tech.' },
      { icon: '🚗', title: 'Automotive Hub', description: 'Home to Renault, Volkswagen, Volvo factories. Strong automotive and embedded tech talent.' },
      { icon: '💰', title: '65% Cost Savings', description: '$18-30/hr vs $80-150/hr in US. Quality engineering at Latin American rates.' },
      { icon: '⏰', title: 'US Timezone', description: 'BRT (GMT-3) offers full overlap with US East Coast business hours.' },
      { icon: '🎓', title: 'UFPR & PUC-PR', description: 'Federal University of Paraná and PUC-PR produce top-tier CS and engineering graduates.' },
      { icon: '🌿', title: 'Quality of Life', description: 'Best quality of life index in Brazil. Low turnover, high retention.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Curitiba over São Paulo?', answer: 'Curitiba offers 15-20% lower costs, 50% lower attrition, best quality of life in Brazil, and strong automotive/IoT specialization.' },
      { question: 'What is the timezone overlap with the US?', answer: 'BRT (GMT-3) provides 6-8 hours overlap with US East Coast. Perfect for nearshore collaboration.' },
      { question: 'Do your Curitiba engineers speak English?', answer: 'Yes. All engineers on international projects are English-proficient (B2/C1+). Portuguese is primary language.' },
      { question: 'What industries does Curitiba specialize in?', answer: 'Automotive, smart mobility, IoT, urban tech, agritech, and fintech — aligned with the regional economy.' },
      { question: 'How fast can you scale?', answer: 'Team setup in 2-3 weeks. Curitiba\'s university pipeline allows rapid scaling.' },
      { question: 'Data protection compliance?', answer: 'LGPD (Brazilian GDPR) compliant, Enterprise Security Standards aligned, and experienced with EU/US data requirements.' },
    ],
    localRate: '$60-100',
    ourRate: '$18-30',
    savings: '65%+',
  },

  krakow: {
    slug: 'software-development-agency-krakow',
    cityName: 'Kraków',
    countryName: 'Poland',
    flag: '🇵🇱',
    timezone: 'CET (GMT+1)',
    currency: 'PLN/EUR',
    title: {
      en: 'Software Development Agency Kraków | Call IT Dev',
      fr: 'Agence Développement Logiciel Cracovie | Call IT Dev',
      de: 'Softwareentwicklung Agentur Krakau | Call IT Dev',
      pl: 'Agencja Rozwoju Oprogramowania Kraków | Call IT Dev',
    },
    description: {
      en: 'Software development agency in Kraków, Poland. EU tech hub, CET timezone, 300+ tech companies. From $25/hr.',
      fr: 'Agence de développement logiciel à Cracovie, Pologne. Hub tech UE, fuseau CET, 300+ entreprises tech, dès 25 $/h.',
      de: 'Softwareentwicklungsagentur in Krakau, Polen. EU-Tech-Hub, CET-Zeitzone, 300+ Tech-Firmen, ab 25 $/h.',
      pl: 'Agencja rozwoju oprogramowania w Krakowie. Hub technologiczny UE, strefa CET, 300+ firm IT, od 25 $/h.',
    },
    keywords: ['software development Kraków', 'IT outsourcing Poland', 'nearshore development Kraków', 'agencja IT Kraków'],
    heroTitle: {
      en: 'Software Development Agency in Kraków',
      fr: 'Agence de Développement Logiciel à Cracovie',
      de: 'Softwareentwicklung Agentur in Krakau',
      pl: 'Agencja Rozwoju Oprogramowania w Krakowie',
    },
    heroSubtitle: {
      en: "Poland's #1 tech city. 300+ tech companies, AGH & Jagiellonian University talent, CET timezone, EU data compliance. 50% savings vs Western Europe.",
      fr: "Ville tech #1 de Pologne. 300+ entreprises tech, talents universitaires, fuseau CET, 50% d'économies.",
      de: 'Polens #1 Tech-Stadt. 300+ Tech-Firmen, AGH & Jagiellonen-Uni, CET, 50% Ersparnis vs Westeuropa.',
      pl: 'Nr 1 miasto tech w Polsce. 300+ firm tech, AGH i UJ, strefa CET, 50% oszczędności vs Europa Zachodnia.',
    },
    whyTitle: {
      en: 'Why Kraków for Software Development',
      fr: 'Pourquoi Cracovie pour le Développement Logiciel',
      de: 'Warum Krakau für Softwareentwicklung',
      pl: 'Dlaczego Kraków do Rozwoju Oprogramowania',
    },
    whyReasons: [
      { icon: '🎓', title: 'World-Class Universities', description: 'AGH University of Science and Jagiellonian University produce 15K+ STEM graduates annually.' },
      { icon: '🇪🇺', title: 'EU Member State', description: 'Full GDPR compliance, EU data sovereignty, Schengen zone. Zero cross-border friction.' },
      { icon: '💰', title: '50% Cost Savings', description: '€25-45/hr vs €80-120/hr in Western Europe. Top quality at Polish rates.' },
      { icon: '⏰', title: 'CET Timezone', description: 'Same timezone as Berlin, Paris, Madrid. Real-time collaboration, no compromises.' },
      { icon: '🏢', title: 'Tech Ecosystem', description: 'Google, IBM, HSBC, ABB all have R&D centers in Kraków. Proven tech city.' },
      { icon: '✈️', title: 'Well Connected', description: 'Direct flights to 100+ European cities. 2h from London, Berlin, Vienna.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Why Kraków for tech?', answer: 'Kraków is Poland\'s #1 tech city with 300+ tech companies, Google & IBM R&D centers, and top universities (AGH, Jagiellonian).' },
      { question: 'How does Kraków compare to Warsaw?', answer: 'Kraków has a denser tech ecosystem relative to city size, 10-15% lower costs, and better talent retention.' },
      { question: 'EU compliance?', answer: 'Full GDPR compliance as EU member state. Data stays in EU. No additional data transfer agreements needed.' },
      { question: 'What is the cost comparison?', answer: '€25-45/hr vs €80-120/hr in Western Europe. 50% savings with EU-level quality and compliance.' },
      { question: 'Can you handle enterprise clients?', answer: 'Yes. Our Kraków team works with Fortune 500 companies on large-scale enterprise projects.' },
      { question: 'How fast can you start?', answer: 'Team operational in 2 weeks. Kraków\'s deep talent pool enables rapid scaling.' },
    ],
    localRate: '€80-120',
    ourRate: '€25-45',
    savings: '50%+',
  },

  marseille: {
    slug: 'software-development-agency-marseille',
    cityName: 'Marseille',
    countryName: 'France',
    flag: '🇫🇷',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Marseille | Call IT Dev',
      fr: 'Agence de Développement Informatique Marseille | Call IT Dev',
      es: 'Agencia Desarrollo Software Marsella | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Marseille. French-speaking engineers, CET timezone, maritime & logistics tech. From €25/hr.',
      fr: 'Agence de développement informatique pour Marseille. Ingénieurs francophones, fuseau CET, tech maritime & logistique, dès 25 €/h.',
      es: 'Agencia de desarrollo de software para Marsella. Ingenieros francófonos, zona CET, tech marítima, desde 25 €/h.',
    },
    keywords: ['agence développement informatique Marseille', 'software development Marseille', 'nearshore development Marseille', 'développeur Marseille'],
    heroTitle: {
      en: 'Software Development Agency for Marseille',
      fr: 'Agence de Développement Informatique pour Marseille',
      es: 'Agencia de Desarrollo de Software para Marsella',
    },
    heroSubtitle: {
      en: "French-speaking engineers at nearshore rates for Marseille businesses. Same timezone, maritime & logistics tech expertise, 60% savings vs local agencies.",
      fr: "Ingénieurs francophones à tarifs nearshore pour les entreprises marseillaises. Même fuseau, expertise maritime & logistique, 60% d'économies.",
      es: 'Ingenieros francófonos a precios nearshore para empresas de Marsella. Misma zona horaria, 60% ahorro.',
    },
    whyTitle: {
      en: 'Why Marseille Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises Marseillaises Choisissent Call IT Dev',
      es: 'Por Qué las Empresas de Marsella Eligen Call IT Dev',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Native French Engineers', description: 'All developers speak native French. Seamless communication for Marseille teams.' },
      { icon: '⏰', title: 'Identical Timezone', description: 'CET timezone — same as Marseille. Real-time collaboration, zero lag.' },
      { icon: '💰', title: '60% Cost Savings', description: '€25-40/hr vs €70-100/hr in Marseille. Same quality at nearshore prices.' },
      { icon: '🚢', title: 'Maritime Tech', description: 'Deep expertise in maritime logistics, port management systems, and shipping tech.' },
      { icon: '✈️', title: '2h Direct Flight', description: 'Casablanca-Marseille: direct flights under 2 hours. Easy for onsite visits.' },
      { icon: '🌍', title: 'Mediterranean Connection', description: 'Shared Mediterranean culture. Strong business ties between Morocco and Marseille.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How close is Casablanca to Marseille?', answer: 'Under 2 hours by direct flight. Multiple weekly connections. Easy for kickoffs and quarterly reviews.' },
      { question: 'Do you work with maritime companies?', answer: 'Yes. We build port management systems, shipping logistics platforms, vessel tracking, and maritime IoT solutions.' },
      { question: 'Cost comparison to Marseille agencies?', answer: '€25-40/hr vs €70-100/hr locally. 60% savings with native French communication and same timezone.' },
      { question: 'Do you comply with French regulations?', answer: 'Yes. RGPD compliant, enterprise-grade security. We understand French business culture and legal requirements.' },
      { question: 'Can you integrate with existing teams?', answer: 'Absolutely. Staff augmentation model — your Jira, your Slack, your Git workflow. Fully embedded.' },
      { question: 'What is the minimum engagement?', answer: 'Start with 2 developers. Scale based on project needs. T&M and fixed-price available.' },
    ],
    localRate: '€70-100',
    ourRate: '€25-40',
    savings: '60%+',
  },
};
